// shortcutCycle — CSP流「同じキーを割り当てた対象同士が押すたびに循環する」の
// 純粋な解決ロジック (docs/_wip/shortcut-settings.md「CSP の実モデル」2026-08-21 確定)。
//
// 割当先の粒度は「個別ペン (`ShortcutAction.penRef`)」「タブ (`ShortcutAction.tabRef`)」
// 「それ以外の通常 action」の 3 種類。**割当先の種類は循環の可否に影響しない** (2026-08-21
// 訂正: 消しゴムも他の action と同じくタブに属さないフラットな対象として循環に参加する)。
// タブだけが「到達時にそのタブで最後に使ったペンを選ぶ」という特別な解決規則を持つが、
// それは呼び出し側 (`useLayoutKeyboardShortcuts` → `chrome.onSelectTabRef`) が action の
// callback を呼ぶ段で担う — 本モジュールは「次にどの action を発火させるか」だけを決める。
//
// ## アルゴリズム (作者確定仕様 1〜6 の一般化)
//
// `candidates` は「同じキーに割り当たっている action 群」を **registry 登録順
// (= 設定画面の並び順、shortcut-settings.md「循環順序はシステム決め打ち」) のまま**
// 渡す。1 回の押下につき:
//
// 1. **現在位置の導出**: `candidates` の中に「`penRef` が現在選択中の pen と一致する
//    もの」があれば、その index を現在位置とする (= 仕様1/2: その個別ペンが選択されて
//    いなければ選ぶ、既に選択されていれば次へ。仕様3/4: 複数ペンが同キーなら選択中の
//    ものを基準に次へ)。`tabRef` を持つ candidate はこの判定に参加しない — タブそのものが
//    「選択中」という状態を持つことは無く、タブ選択の結果として具体的な pen が選ばれる
//    だけなので (rule 6 参照)。
// 2. **一致しなければ記憶した前回位置** (`ctx.rememberedIndex`) を使う (= 仕様6:
//    到達先のペン — 例えばタブ経由で選ばれたペン — がそのキーの割当集合に含まれず、
//    現在位置を現在ペンから導けないとき)。記憶が無ければ `-1` 扱い (次 index が 0 になる
//    = 初回押下は常に先頭から)。
// 3. **次 index = (現在位置 + 1) mod 件数** を発火する。
//
// `candidates.length === 1` (= 既定キーマップの通常ケース、循環未使用) でも同じ経路を
// 通り、必ずその 1 件を返す (= 既定挙動を壊さない)。

import type { StrokePen } from '@oekaki/protocol';

import type { ShortcutAction } from './shortcutRegistry.js';

export interface ShortcutCycleContext {
  /** 現在選択中の pen (eraser 含む)。 */
  readonly currentPen: StrokePen;
  /** このキーについて記憶している前回解決の index。記憶が無ければ `null`。 */
  readonly rememberedIndex: number | null;
}

export interface ShortcutCycleResult {
  /** 今回発火する action。 */
  readonly action: ShortcutAction;
  /** このキーの次回解決のために記憶する index。 */
  readonly nextRememberedIndex: number;
}

/**
 * 1 つのキー押下に割り当たっている action 群 (`candidates`、1 件以上・登録順) から、
 * 次に発火する 1 件を解決する。呼び出し側は `candidates.length >= 1` を保証すること
 * (0 件なら呼ばない = 「そのキーに何も割り当たっていない」)。
 */
export const resolveShortcutCycle = (
  candidates: readonly ShortcutAction[],
  ctx: ShortcutCycleContext,
): ShortcutCycleResult => {
  const n = candidates.length;
  if (n === 0) {
    throw new Error('resolveShortcutCycle: candidates は 1 件以上である必要があります');
  }
  let currentIndex = candidates.findIndex(
    (a) => a.penRef !== undefined && a.penRef === ctx.currentPen,
  );
  if (currentIndex === -1) currentIndex = ctx.rememberedIndex ?? -1;
  const nextIndex = (((currentIndex + 1) % n) + n) % n;
  const action = candidates[nextIndex];
  if (action === undefined) {
    // n>=1 かつ nextIndex は 0..n-1 に正規化済みなので理論上到達しない防御的分岐。
    throw new Error('resolveShortcutCycle: 内部不変条件違反 (nextIndex out of range)');
  }
  return { action, nextRememberedIndex: nextIndex };
};
